import { IAftBayLayout, IAftBayLayoutRow } from "@tedivo/tedivo-bay-grid-pure";
import { ITransponserFunctions, IUnitsConverterFunctions, roundDec } from "@tedivo/tedivo-pure-helpers";

import { createSvgText } from "../helpers/createSvgText";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";

const SVG_NS = "http://www.w3.org/2000/svg";
const MONO_FONT_FAMILY = `"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace`;
const CG_NUMBER_LETTER_SPACING = "-0.05em";

const TCG_LABEL_BOTTOM_PADDING = 16;
const BOTTOM_BASE_SIDE_LABEL_WIDTH = 40;
/** Exterior width of a container, in mm. Only used to draw the `#cntAftView` symbol. */
const CONTAINER_WIDTH_FOR_SYMBOL_MM = 2440;
/** Height of a stacked tier box, in mm, when the layout doesn't vary it. Matches
 * `calculateAftBayLayout`'s default `getTierHeightMm` (CONTAINER_HEIGHT_IN_MMM). */
const DEFAULT_TIER_HEIGHT_FOR_SYMBOL_MM = 2591;

export interface IAftDiagramColors {
  textColor: string;
  containerFill: string;
  containerStroke: string;
  tcgStroke: string;
  bbStroke: string;
  thStroke: string;
  portColor: string;
  stbdColor: string;
  zeroTcgStroke: string;
}

/**
 * Draws a bay's aft/cross-section diagram (rows placed by TCG, tiers stacked from
 * each row's bottom base) into `svg`, from a layout calculated by `calculateAftBayLayout`
 * (tedivo-bay-grid-pure). Mutates and returns `svg`, or `undefined` if the layout can't
 * be drawn (degenerate width/height).
 */
export function drawAftBayDiagram({
  layout,
  svg,
  colors,
  units,
  svgWidth = 400,
}: {
  layout: IAftBayLayout;
  svg: SVGElement;
  colors: IAftDiagramColors;
  units: {
    lengthUnits: IUnitsConverterFunctions<unknown>;
    tcgTransposer: ITransponserFunctions<unknown>;
    vcgTransposer: ITransponserFunctions<unknown>;
  };
  /** SVG user-unit width the diagram is scaled to. Default 400 (matches the original diagram). */
  svgWidth?: number;
}): SVGElement | undefined {
  removeChildren(svg);

  const { widthMm, minBB, maxBB, addXMm, rows } = layout;

  const scaled = (n: number) => roundDec((svgWidth / widthMm) * n, 2);
  const height = scaled(maxBB - minBB + 3000);
  const svgHeight = height + TCG_LABEL_BOTTOM_PADDING;

  if (isNaN(widthMm) || isNaN(height)) return undefined;

  const scaledTierHeight = scaled(DEFAULT_TIER_HEIGHT_FOR_SYMBOL_MM);
  const scaledContainerWidth = scaled(CONTAINER_WIDTH_FOR_SYMBOL_MM);

  const bottomBasesPathParts: string[] = [];
  const maxHeigtsPathParts: string[] = [];

  const rowTitles: SVGTextElement[] = [];
  const containers: SVGElement[] = [];
  const tcgPoints: SVGElement[] = [];

  // bb value -> {y, outerX}; rows are visited port-outer-to-inner then stbd-inner-to-outer,
  // so "first wins" on port and "last wins" on stbd both keep the outermost row.
  const portBottomBaseLabels = new Map<number, { y: number; outerX: number }>();
  const stbdBottomBaseLabels = new Map<number, { y: number; outerX: number }>();

  const textColor = colors.textColor;

  rows.forEach((row: IAftBayLayoutRow) => {
    const { tcg, prevTcgMid, nextTcgMid, bottomBaseMm, maxHeightMm, isPort, tierBasesMm } = row;

    const y = roundDec(height - scaled(-minBB + bottomBaseMm) - 10);

    if (bottomBasesPathParts.length === 0) {
      bottomBasesPathParts.push(`M${scaled(prevTcgMid + addXMm)},${y}`);
    }

    bottomBasesPathParts.push(`L${scaled(prevTcgMid + addXMm)},${y}`);
    bottomBasesPathParts.push(`L${scaled(nextTcgMid + addXMm)},${y}`);

    if (isPort) {
      if (!portBottomBaseLabels.has(bottomBaseMm))
        portBottomBaseLabels.set(bottomBaseMm, { y, outerX: scaled(prevTcgMid + addXMm) });
    } else {
      stbdBottomBaseLabels.set(bottomBaseMm, { y, outerX: scaled(nextTcgMid + addXMm) });
    }

    rowTitles.push(
      createSvgText({
        text: `${row.row}${row.bottomTier}`,
        x: scaled(tcg + addXMm),
        y: y - 6,
        fontSize: 6,
        textColor,
        letterSpacing: CG_NUMBER_LETTER_SPACING,
      }),
    );

    if (maxHeightMm !== undefined) {
      const h = -minBB + maxHeightMm + bottomBaseMm;
      maxHeigtsPathParts.push(
        `M${scaled(prevTcgMid + addXMm)},${height - scaled(h) - 10} L${scaled(
          nextTcgMid + addXMm,
        )},${height - scaled(h) - 10}`,
      );
    }

    tierBasesMm.forEach((tierBaseMm) => {
      const contSvg = document.createElementNS(SVG_NS, "use");

      contSvg.setAttribute("href", `#cntAftView`);
      contSvg.setAttribute("x", `${scaled(tcg + addXMm)}`);
      contSvg.setAttribute("y", `${roundDec(height - scaled(-minBB + tierBaseMm) - 10)}`);

      containers.push(contSvg);
    });

    const tcgPoint = document.createElementNS(SVG_NS, "use");
    tcgPoint.setAttribute("href", `#tcgPoint`);
    tcgPoint.setAttribute("x", `${scaled(tcg + addXMm)}`);
    tcgPoint.setAttribute("y", `${y - 2}`);

    tcgPoints.push(tcgPoint);

    const tcgValueText = createSvgText({
      text: units.lengthUnits.fromValueToFormattedDisplay(
        units.tcgTransposer.fromValueToDisplay(tcg),
      ),
      x: scaled(tcg + addXMm),
      y: y + 10,
      fontSize: 5,
      textColor: isPort ? colors.portColor : colors.stbdColor,
      fontFamily: MONO_FONT_FAMILY,
      letterSpacing: CG_NUMBER_LETTER_SPACING,
    });

    tcgPoints.push(tcgValueText);
  });

  svg.setAttribute("width", `${svgWidth + BOTTOM_BASE_SIDE_LABEL_WIDTH * 2}px`);
  svg.setAttribute("height", `${svgHeight}px`);
  svg.setAttribute(
    "viewBox",
    `${-BOTTOM_BASE_SIDE_LABEL_WIDTH} 0 ${svgWidth + BOTTOM_BASE_SIDE_LABEL_WIDTH * 2} ${svgHeight}`,
  );

  const zeroTcgLine = document.createElementNS(SVG_NS, "line");
  zeroTcgLine.setAttribute("x1", `${scaled(addXMm)}`);
  zeroTcgLine.setAttribute("y1", "0");
  zeroTcgLine.setAttribute("x2", `${scaled(addXMm)}`);
  zeroTcgLine.setAttribute("y2", `${svgHeight}`);
  zeroTcgLine.setAttribute("stroke", colors.zeroTcgStroke);
  zeroTcgLine.setAttribute("stroke-width", "1");
  zeroTcgLine.setAttribute("stroke-dasharray", "1,1");
  zeroTcgLine.setAttribute("vector-effect", "non-scaling-stroke");
  svg.appendChild(zeroTcgLine);

  const container86Symbol = document.createElementNS(SVG_NS, "symbol");
  const rect = document.createElementNS(SVG_NS, "path");
  rect.setAttribute(
    "d",
    `M${-scaled(CONTAINER_WIDTH_FOR_SYMBOL_MM * 0.5)},${-scaledTierHeight} h${scaledContainerWidth} v${scaledTierHeight} h-${scaledContainerWidth} z`,
  );
  rect.setAttribute("fill", colors.containerFill);
  rect.setAttribute("stroke", colors.containerStroke);
  rect.setAttribute("stroke-width", "0.5");
  rect.setAttribute("vector-effect", "non-scaling-stroke");

  container86Symbol.id = `cntAftView`;
  container86Symbol.setAttribute("style", "overflow:visible");
  container86Symbol.appendChild(rect);
  svg.appendChild(container86Symbol);

  const tcgMarkerSymbol = document.createElementNS(SVG_NS, "symbol");
  const tcgPoint = document.createElementNS(SVG_NS, "line");
  tcgPoint.setAttribute("x1", "0");
  tcgPoint.setAttribute("y1", "-2");
  tcgPoint.setAttribute("x2", "0");
  tcgPoint.setAttribute("y2", "5");
  tcgPoint.setAttribute("stroke", colors.tcgStroke);
  tcgPoint.setAttribute("stroke-width", "3");
  tcgPoint.setAttribute("vector-effect", "non-scaling-stroke");

  tcgMarkerSymbol.id = "tcgPoint";
  tcgMarkerSymbol.appendChild(tcgPoint);
  svg.appendChild(tcgMarkerSymbol);

  containers.forEach((cnt) => {
    svg.appendChild(cnt);
  });

  const svgBb = document.createElementNS(SVG_NS, "path");
  svgBb.setAttribute("stroke", colors.bbStroke);
  svgBb.setAttribute("stroke-width", "1");
  svgBb.setAttribute("fill-opacity", "0");
  svgBb.setAttribute("class", "grid-lines");
  svgBb.setAttribute("stroke-linecap", "round");
  svgBb.setAttribute("vector-effect", "non-scaling-stroke");
  svgBb.setAttribute("d", bottomBasesPathParts.join(" "));
  svg.appendChild(svgBb);

  portBottomBaseLabels.forEach(({ y, outerX }, bb) => {
    const connector = document.createElementNS(SVG_NS, "line");
    connector.setAttribute("x1", `${outerX}`);
    connector.setAttribute("y1", `${y}`);
    connector.setAttribute("x2", "-6");
    connector.setAttribute("y2", `${y}`);
    connector.setAttribute("stroke", colors.bbStroke);
    connector.setAttribute("stroke-width", "0.5");
    connector.setAttribute("stroke-dasharray", "1,1");
    connector.setAttribute("vector-effect", "non-scaling-stroke");
    svg.appendChild(connector);

    svg.appendChild(
      createSvgText({
        text: units.lengthUnits.fromValueToFormattedDisplay(units.vcgTransposer.fromValueToDisplay(bb)),
        x: -6,
        y,
        fontSize: 5,
        textColor: colors.bbStroke,
        textAnchor: "end",
        fontFamily: MONO_FONT_FAMILY,
        letterSpacing: CG_NUMBER_LETTER_SPACING,
      }),
    );
  });

  stbdBottomBaseLabels.forEach(({ y, outerX }, bb) => {
    const connector = document.createElementNS(SVG_NS, "line");
    connector.setAttribute("x1", `${outerX}`);
    connector.setAttribute("y1", `${y}`);
    connector.setAttribute("x2", `${svgWidth + 6}`);
    connector.setAttribute("y2", `${y}`);
    connector.setAttribute("stroke", colors.bbStroke);
    connector.setAttribute("stroke-width", "0.5");
    connector.setAttribute("stroke-dasharray", "1,1");
    connector.setAttribute("vector-effect", "non-scaling-stroke");

    svg.appendChild(connector);

    svg.appendChild(
      createSvgText({
        text: units.lengthUnits.fromValueToFormattedDisplay(units.vcgTransposer.fromValueToDisplay(bb)),
        x: svgWidth + 6,
        y,
        fontSize: 5,
        textColor: colors.bbStroke,
        textAnchor: "start",
        fontFamily: MONO_FONT_FAMILY,
        letterSpacing: CG_NUMBER_LETTER_SPACING,
      }),
    );
  });

  const svgTh = document.createElementNS(SVG_NS, "path");
  svgTh.setAttribute("stroke", colors.thStroke);
  svgTh.setAttribute("stroke-width", "1");
  svgTh.setAttribute("fill-opacity", "0");
  svgTh.setAttribute("class", "grid-lines");
  svgTh.setAttribute("stroke-linecap", "round");
  svgTh.setAttribute("d", maxHeigtsPathParts.join(" "));
  svg.appendChild(svgTh);

  rowTitles.forEach((svgText) => {
    svg.appendChild(svgText);
  });

  tcgPoints.forEach((point) => {
    svg.appendChild(point);
  });

  return svg;
}
